import * as pc from 'playcanvas';
import { Ref } from '../types';

const EPSILON = 1e-5;
export const PI = Math.PI;
export const TWO_PI = Math.PI * 2;
export const HALF_PI = Math.PI / 2;
export const DEG2RAD = Math.PI / 180;
export const RAD2DEG = 180 / Math.PI;

export function clamp( value: number, min: number, max: number ) {

	return Math.max( min, Math.min( max, value ) );

}

export function approxZero( number: number, error: number = EPSILON ): boolean {

	return Math.abs( number ) < error;

}

export function approxEquals( a: number, b: number, error: number = EPSILON ): boolean {

	return approxZero( a - b, error );

}

export function roundToStep( value: number, step: number ): number {

	return Math.round( value / step ) * step;

}

export function infinityToMaxNumber( value: number ): number {

	if ( isFinite( value ) ) return value;

	if ( value < 0 ) return - Number.MAX_VALUE;

	return Number.MAX_VALUE;

}

export function maxNumberToInfinity( value: number ): number {

	if ( Math.abs( value ) < Number.MAX_VALUE ) return value;

	return value * Infinity;

}

// https://docs.unity3d.com/ScriptReference/Mathf.SmoothDamp.html
// https://github.com/Unity-Technologies/UnityCsReference/blob/a2bdfe9b3c4cd4476f44bf52f848063bfaf7b6b9/Runtime/Export/Math/Mathf.cs#L308
export function smoothDamp(
	current: number,
	target: number,
	currentVelocityRef: Ref,
	smoothTime: number,
	maxSpeed: number = Infinity,
	deltaTime: number,
): number {

	// Based on Game Programming Gems 4 Chapter 1.10
	smoothTime = Math.max( 0.0001, smoothTime );
	const omega = 2 / smoothTime;

	const x = omega * deltaTime;
	const exp = 1 / ( 1 + x + 0.48 * x * x + 0.235 * x * x * x );
	let change = current - target;
	const originalTo = target;

	// Clamp maximum speed
	const maxChange = maxSpeed * smoothTime;
	change = clamp( change, - maxChange, maxChange );
	target = current - change;

	const temp = ( currentVelocityRef.value + omega * change ) * deltaTime;
	currentVelocityRef.value = ( currentVelocityRef.value - omega * temp ) * exp;
	let output = target + ( change + temp ) * exp;

	// Prevent overshooting
	if ( originalTo - current > 0.0 === output > originalTo ) {

		output = originalTo;
		currentVelocityRef.value = ( output - originalTo ) / deltaTime;

	}

	return output;

}

// https://docs.unity3d.com/ScriptReference/Vector3.SmoothDamp.html
// https://github.com/Unity-Technologies/UnityCsReference/blob/a2bdfe9b3c4cd4476f44bf52f848063bfaf7b6b9/Runtime/Export/Math/Vector3.cs#L97
export function smoothDampVec3(
	current: pc.Vec3,
	target: pc.Vec3,
	currentVelocityRef: pc.Vec3,
	smoothTime: number,
	maxSpeed: number = Infinity,
	deltaTime: number,
	out: pc.Vec3
) {

	// Based on Game Programming Gems 4 Chapter 1.10
	smoothTime = Math.max( 0.0001, smoothTime );
	const omega = 2 / smoothTime;

	const x = omega * deltaTime;
	const exp = 1 / ( 1 + x + 0.48 * x * x + 0.235 * x * x * x );

	let targetX = target.x;
	let targetY = target.y;
	let targetZ = target.z;

	let changeX = current.x - targetX;
	let changeY = current.y - targetY;
	let changeZ = current.z - targetZ;

	const originalToX = targetX;
	const originalToY = targetY;
	const originalToZ = targetZ;

	// Clamp maximum speed
	const maxChange = maxSpeed * smoothTime;

	const maxChangeSq = maxChange * maxChange;
	const magnitudeSq = changeX * changeX + changeY * changeY + changeZ * changeZ;

	if ( magnitudeSq > maxChangeSq ) {

		const magnitude = Math.sqrt( magnitudeSq );
		changeX = changeX / magnitude * maxChange;
		changeY = changeY / magnitude * maxChange;
		changeZ = changeZ / magnitude * maxChange;

	}

	targetX = current.x - changeX;
	targetY = current.y - changeY;
	targetZ = current.z - changeZ;

	const tempX = ( currentVelocityRef.x + omega * changeX ) * deltaTime;
	const tempY = ( currentVelocityRef.y + omega * changeY ) * deltaTime;
	const tempZ = ( currentVelocityRef.z + omega * changeZ ) * deltaTime;

	currentVelocityRef.x = ( currentVelocityRef.x - omega * tempX ) * exp;
	currentVelocityRef.y = ( currentVelocityRef.y - omega * tempY ) * exp;
	currentVelocityRef.z = ( currentVelocityRef.z - omega * tempZ ) * exp;

	out.x = targetX + ( changeX + tempX ) * exp;
	out.y = targetY + ( changeY + tempY ) * exp;
	out.z = targetZ + ( changeZ + tempZ ) * exp;

	// Prevent overshooting
	const origMinusCurrentX = originalToX - current.x;
	const origMinusCurrentY = originalToY - current.y;
	const origMinusCurrentZ = originalToZ - current.z;
	const outMinusOrigX = out.x - originalToX;
	const outMinusOrigY = out.y - originalToY;
	const outMinusOrigZ = out.z - originalToZ;

	if ( origMinusCurrentX * outMinusOrigX + origMinusCurrentY * outMinusOrigY + origMinusCurrentZ * outMinusOrigZ > 0 ) {

		out.x = originalToX;
		out.y = originalToY;
		out.z = originalToZ;

		currentVelocityRef.x = ( out.x - originalToX ) / deltaTime;
		currentVelocityRef.y = ( out.y - originalToY ) / deltaTime;
		currentVelocityRef.z = ( out.z - originalToZ ) / deltaTime;

	}

	return out;

}

export function smoothDampQuat(
	current: pc.Quat,
	target: pc.Quat,
	currentVelocityRef: pc.Quat, // We use Quat to store the rate of change of the 4 components
	smoothTime: number,
	deltaTime: number,
	out: pc.Quat
): pc.Quat {
	smoothTime = Math.max(0.0001, smoothTime);
	const omega = 2 / smoothTime;

	const x = omega * deltaTime;
	const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);

	let targetX = target.x;
	let targetY = target.y;
	let targetZ = target.z;
	let targetW = target.w;

	// Ensure shortest path
	if (current.dot(target) < 0) {
		targetX = -targetX;
		targetY = -targetY;
		targetZ = -targetZ;
		targetW = -targetW;
	}

	let changeX = current.x - targetX;
	let changeY = current.y - targetY;
	let changeZ = current.z - targetZ;
	let changeW = current.w - targetW;

	// We don't clamp max speed for rotation in this simple implementation 
	// as it's less critical for camera smoothing than position, 
	// and "maxSpeed" for quaternions is tricky (angular velocity).

	targetX = current.x - changeX;
	targetY = current.y - changeY;
	targetZ = current.z - changeZ;
	targetW = current.w - changeW;

	const tempX = (currentVelocityRef.x + omega * changeX) * deltaTime;
	const tempY = (currentVelocityRef.y + omega * changeY) * deltaTime;
	const tempZ = (currentVelocityRef.z + omega * changeZ) * deltaTime;
	const tempW = (currentVelocityRef.w + omega * changeW) * deltaTime;

	currentVelocityRef.x = (currentVelocityRef.x - omega * tempX) * exp;
	currentVelocityRef.y = (currentVelocityRef.y - omega * tempY) * exp;
	currentVelocityRef.z = (currentVelocityRef.z - omega * tempZ) * exp;
	currentVelocityRef.w = (currentVelocityRef.w - omega * tempW) * exp;

	out.x = targetX + (changeX + tempX) * exp;
	out.y = targetY + (changeY + tempY) * exp;
	out.z = targetZ + (changeZ + tempZ) * exp;
	out.w = targetW + (changeW + tempW) * exp;

	// Prevent overshooting (simple dot product check might not be enough for 4D, 
	// but we can check if we passed the target).
	// For quaternions, overshooting isn't as simple as 1D. 
	// We'll skip the overshoot clamp for now to ensure smoothness, 
	// or we could implement it if needed. 
	// Given the user's request for "single integrator", the standard smoothDamp is fine.

	return out.normalize();
}

export function setQuatFromUnitVectors(q: pc.Quat, vFrom: pc.Vec3, vTo: pc.Vec3): pc.Quat {
    let r = vFrom.dot(vTo) + 1;
    if (r < 1e-6) {
        r = 0;
        if (Math.abs(vFrom.x) > Math.abs(vFrom.z)) {
            q.set(-vFrom.y, vFrom.x, 0, r);
        } else {
            q.set(0, -vFrom.z, vFrom.y, r);
        }
    } else {
        q.set(
            vFrom.y * vTo.z - vFrom.z * vTo.y,
            vFrom.z * vTo.x - vFrom.x * vTo.z,
            vFrom.x * vTo.y - vFrom.y * vTo.x,
            r
        );
    }
    return q.normalize();
}
